import platformSingleton from '../../../../platform/platformSingleton';

// Components
import CenterTextTool from '../../components/selectionTools/editorTools/CenterTextTool';
import HyperlinkTool from '../../components/selectionTools/editorTools/HyperlinkTool';
import TextStyleTool from '../../components/selectionTools/editorTools/TextStyleTool';
import TableTextStyleTool from '../../components/selectionTools/editorTools/TableTextStyleTool';
import AssignUserTool from '../../components/selectionTools/assignmentTools/AssignUserTool';
import DueDateTool from '../../components/selectionTools/assignmentTools/DueDateTool';
import TableCellAlignmentTool from '../../components/selectionTools/tableTools/TableCellAlignmentTool';
import MobileTableCellAlignmentTool from '../../components/selectionTools/tableTools/MobileTableCellAlignmentTool';
import TableCellFormulaTool from '../../components/selectionTools/tableTools/TableCellFormulaTool';
import AddColumnTool from '../../components/selectionTools/tableTools/AddColumnTool';
import AddRowTool from '../../components/selectionTools/tableTools/AddRowTool';
import CellTypeTool from '../../components/selectionTools/tableTools/CellTypeTool';
import MobileCellTypeTool from '../../components/selectionTools/tableTools/MobileCellTypeTool';
import MobileCellTypeFormatTool from '../../components/selectionTools/tableTools/MobileCellTypeFormatTool';
import TableCellColorTool from '../../components/selectionTools/tableTools/TableCellColorTool';
import MobileTableCellColorTool from '../../components/selectionTools/tableTools/MobileTableCellColorTool';
import MobileTextStyleRichTextTool from '../../../../mobile/toolbar/tools/richTextTool/MobileTextStyleRichTextTool';
import MobileTableTextStyleRichTextTool from '../../../../mobile/toolbar/tools/richTextTool/MobileTableTextStyleRichTextTool';
import BoldTool from '../../components/selectionTools/editorTools/BoldTool';
import ItalicTool from '../../components/selectionTools/editorTools/ItalicTool';
import StrikeTool from '../../components/selectionTools/editorTools/StrikeTool';
import OrderedListTool from '../../components/selectionTools/editorTools/OrderedListTool';
import BulletedListTool from '../../components/selectionTools/editorTools/BulletedListTool';
import IndentTaskTool from '../../components/selectionTools/editorTools/IndentTaskTool';
import UnindentTaskTool from '../../components/selectionTools/editorTools/UnindentTaskTool';
import UnindentTextTool from '../../components/selectionTools/editorTools/TiptapUnindentTextTool';
import IndentTextTool from '../../components/selectionTools/editorTools/TiptapIndentTextTool';
import UnderlineTool from '../../components/selectionTools/editorTools/UnderlineTool';
import InlineCodeTool from '../../components/selectionTools/editorTools/InlineCodeTool';
import InlineCommentTool from '../../components/selectionTools/editorTools/InlineCommentTool';

// Constants
import { STYLE_COMMANDS } from '../../../../components/editor/richText/richTextConstants';
import { COLLAPSE_ORDER, TOOL_HEIGHT } from '../toolbarToolConfigConstants';

// Types
import { ToolbarItemConfig } from '../../toolbarTypes';
import { DeviceFormFactor, MilanoteApplicationMode } from '../../../../../common/platform/platformTypes';
// We want to be quite specific about the collapse order of these tools, so we define them here
export const TEXT_TOOL_COLLAPSE_ORDER: Record<string, number> = {
    // things we want to collapse late
    TEXT_STYLE: COLLAPSE_ORDER.LATE,
    BOLD_ITALIC: COLLAPSE_ORDER.DEFAULT,
    LIST_STYLES: COLLAPSE_ORDER.DEFAULT + 1,
    // things we should collapse early
    TEXT_HYPERLINK: COLLAPSE_ORDER.EARLY,
    TEXT_INDENT: COLLAPSE_ORDER.EARLY + 1,
    TEXT_ALIGNMENT: COLLAPSE_ORDER.EARLY + 2,
    TEXT_STRIKETHROUGH: COLLAPSE_ORDER.EARLY + 3,
    TEXT_UNDERLINE: COLLAPSE_ORDER.EARLY + 4,
    TEXT_INLINE_CODE: COLLAPSE_ORDER.EARLY + 5,
} as const;

const TEXT_STYLES: ToolbarItemConfig = {
    id: 'TEXT_STYLE',
    name: 'Text style',
    localeKey: 'tools.editing.textStyle',
    height: TOOL_HEIGHT.SQUARE,
    component: TextStyleTool,
    mobileComponent: MobileTextStyleRichTextTool,
    collapseOrder: TEXT_TOOL_COLLAPSE_ORDER.TEXT_STYLE,
    toolSettings: {
        showBlockStyleTools: false,
    },
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const TEXT_BLOCK_STYLES: ToolbarItemConfig = {
    id: 'TEXT_BLOCK_STYLES',
    name: 'Block styles',
    localeKey: 'tools.editing.blockStyles',
    height: TOOL_HEIGHT.SQUARE,
    component: TextStyleTool,
    mobileComponent: MobileTextStyleRichTextTool,
    collapseOrder: TEXT_TOOL_COLLAPSE_ORDER.TEXT_STYLE,
    toolSettings: {
        showBlockStyleTools: true,
    },
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const TEXT_BOLD: ToolbarItemConfig = {
    id: 'TEXT_BOLD',
    name: 'Bold',
    localeKey: 'tools.editing.bold',
    tooltipText: 'Bold',
    tooltipLocaleKey: 'tools.editing.tooltips.bold',
    shortcut: ['command', 'b'],
    iconName: 'toolbar-bold',
    height: TOOL_HEIGHT.SQUARE,
    component: BoldTool,
    collapseOrder: TEXT_TOOL_COLLAPSE_ORDER.BOLD_ITALIC,
    toolSettings: {
        styleCommand: STYLE_COMMANDS.BOLD,
    },
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const TEXT_ITALIC: ToolbarItemConfig = {
    id: 'TEXT_ITALIC',
    name: 'Italic',
    localeKey: 'tools.editing.italic',
    tooltipText: 'Italic',
    tooltipLocaleKey: 'tools.editing.tooltips.italic',
    // Adding a space to visually shift the 1 across slightly
    shortcut: ['command', ' ', ' ', 'i'],
    iconName: 'toolbar-italic',
    component: ItalicTool,
    height: TOOL_HEIGHT.SQUARE,
    collapseOrder: TEXT_TOOL_COLLAPSE_ORDER.BOLD_ITALIC,
    toolSettings: {
        styleCommand: STYLE_COMMANDS.ITALIC,
    },
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const TEXT_STRIKETHROUGH: ToolbarItemConfig = {
    id: 'TEXT_STRIKETHROUGH',
    name: 'Strikethrough',
    localeKey: 'tools.editing.strikethrough',
    tooltipText: 'Strikethrough',
    tooltipLocaleKey: 'tools.editing.tooltips.strikethrough',
    shortcut: ['command', 'shift', 'X'],
    iconName: 'toolbar-strikethrough',
    component: StrikeTool,
    height: TOOL_HEIGHT.SQUARE,
    collapseOrder: TEXT_TOOL_COLLAPSE_ORDER.TEXT_STRIKETHROUGH,
    toolSettings: {
        styleCommand: STYLE_COMMANDS.STRIKETHROUGH,
    },
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const TEXT_UNDERLINE: ToolbarItemConfig = {
    id: 'TEXT_UNDERLINE',
    name: 'Underline',
    localeKey: 'tools.editing.underline',
    tooltipText: 'Underline',
    tooltipLocaleKey: 'tools.editing.tooltips.underline',
    shortcut: ['command', 'u'],
    iconName: 'toolbar-underline',
    component: UnderlineTool,
    height: TOOL_HEIGHT.SQUARE,
    collapseOrder: TEXT_TOOL_COLLAPSE_ORDER.TEXT_UNDERLINE,
    toolSettings: {
        styleCommand: STYLE_COMMANDS.UNDERLINE,
    },
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const TEXT_INLINE_COMMENT: ToolbarItemConfig = {
    id: 'TEXT_INLINE_COMMENT',
    name: 'Comment',
    localeKey: 'tools.editing.comment',
    tooltipText: 'Comment',
    tooltipLocaleKey: 'tools.editing.tooltips.comment',
    iconName: 'toolbar-comment',
    component: InlineCommentTool,
    height: TOOL_HEIGHT.SQUARE,
    collapseOrder: COLLAPSE_ORDER.NEVER,
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const TEXT_INLINE_CODE: ToolbarItemConfig = {
    id: 'TEXT_INLINE_CODE',
    name: 'Inline code',
    localeKey: 'tools.editing.inlineCode',
    tooltipText: 'Inline code',
    tooltipLocaleKey: 'tools.editing.tooltips.inlineCode',
    shortcut: ['command', 'e'],
    iconName: 'toolbar-inline-code',
    component: InlineCodeTool,
    height: TOOL_HEIGHT.SQUARE,
    collapseOrder: TEXT_TOOL_COLLAPSE_ORDER.TEXT_INLINE_CODE,
    toolSettings: {
        styleCommand: STYLE_COMMANDS.INLINE_CODE,
    },
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const TEXT_BULLET_LIST: ToolbarItemConfig = {
    id: 'TEXT_BULLET_LIST',
    name: 'Bullet',
    localeKey: 'tools.editing.bulletList',
    tooltipText: 'Bullet',
    tooltipLocaleKey: 'tools.editing.tooltips.bulletList',
    shortcut: ['command', 'shift', 'u'],
    iconName: 'toolbar-list',
    component: BulletedListTool,
    height: TOOL_HEIGHT.SQUARE,
    collapseOrder: TEXT_TOOL_COLLAPSE_ORDER.LIST_STYLES,
    toolSettings: {
        styleCommand: STYLE_COMMANDS.LIST,
    },
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const TEXT_ORDERED_LIST: ToolbarItemConfig = {
    id: 'TEXT_ORDERED_LIST',
    name: 'Numbered list',
    localeKey: 'tools.editing.numberedList',
    tooltipText: 'Numbered list',
    tooltipLocaleKey: 'tools.editing.tooltips.numberedList',
    shortcut: ['command', 'shift', 'o'],
    iconName: 'toolbar-numlist',
    component: OrderedListTool,
    height: TOOL_HEIGHT.SQUARE,
    collapseOrder: TEXT_TOOL_COLLAPSE_ORDER.LIST_STYLES,
    toolSettings: {
        styleCommand: STYLE_COMMANDS.ORDERED_LIST,
    },
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const TEXT_CENTERED: ToolbarItemConfig = {
    id: 'TEXT_CENTERED',
    name: 'Align center',
    localeKey: 'tools.editing.align.center',
    tooltipText: 'Align center',
    tooltipLocaleKey: 'tools.editing.tooltips.centerText',
    // Adding a space to visually shift the 1 across slightly
    shortcut: ['command', ' ', ' ', '\\'],
    iconName: 'toolbar-center-text',
    component: CenterTextTool,
    height: TOOL_HEIGHT.SQUARE,
    collapseOrder: TEXT_TOOL_COLLAPSE_ORDER.TEXT_ALIGNMENT,
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const TEXT_HYPERLINK: ToolbarItemConfig = {
    id: 'TEXT_HYPERLINK',
    name: 'Hyperlink',
    localeKey: 'tools.editing.hyperlink',
    tooltipText: 'Hyperlink',
    tooltipLocaleKey: 'tools.editing.tooltips.hyperlink',
    shortcut: ['command', 'k'],
    iconName: 'toolbar-hyperlink',
    component: HyperlinkTool,
    height: TOOL_HEIGHT.SQUARE,
    collapseOrder: TEXT_TOOL_COLLAPSE_ORDER.TEXT_HYPERLINK,
    toolSettings: {
        styleCommand: STYLE_COMMANDS.HYPERLINK,
    },
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const TABLE_CELL_ALIGNMENT: ToolbarItemConfig = {
    id: 'TABLE_CELL_ALIGNMENT',
    name: 'Alignment',
    localeKey: 'tools.editing.table.alignment',
    height: TOOL_HEIGHT.SQUARE,
    collapseOrder: COLLAPSE_ORDER.DEFAULT,
    component: TableCellAlignmentTool,
    mobileComponent: MobileTableCellAlignmentTool,
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const TABLE_TEXT_STYLES: ToolbarItemConfig = {
    id: 'TABLE_TEXT_STYLES',
    name: 'Text style',
    localeKey: 'tools.editing.textStyle',
    height: TOOL_HEIGHT.SQUARE,
    component: TableTextStyleTool,
    mobileComponent: MobileTableTextStyleRichTextTool,
    collapseOrder: TEXT_TOOL_COLLAPSE_ORDER.TEXT_STYLE,
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const TABLE_CELL_FORMULA: ToolbarItemConfig = {
    id: 'TABLE_CELL_FORMULA',
    name: 'Formula',
    localeKey: 'tools.editing.table.formula',
    height: TOOL_HEIGHT.SQUARE,
    collapseOrder: COLLAPSE_ORDER.DEFAULT,
    component: TableCellFormulaTool,
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const TABLE_CELL_TYPE: ToolbarItemConfig = {
    id: 'TABLE_CELL_TYPE',
    name: 'Cell type',
    localeKey: 'tools.editing.table.cellType',
    height: TOOL_HEIGHT.SQUARE,
    collapseOrder: COLLAPSE_ORDER.NEVER,
    component: CellTypeTool,
    mobileComponent: MobileCellTypeTool,
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const TABLE_CELL_TYPE_FORMAT: ToolbarItemConfig = {
    id: 'TABLE_CELL_TYPE_FORMAT',
    name: 'Format',
    height: TOOL_HEIGHT.SQUARE,
    collapseOrder: COLLAPSE_ORDER.NEVER,
    component: MobileCellTypeFormatTool,
    appModeAvailability: [MilanoteApplicationMode.mobile],
};

const TABLE_CELL_COLOR: ToolbarItemConfig = {
    id: 'TABLE_CELL_COLOR',
    name: 'Cell Color',
    localeKey: 'tools.editing.table.cellColor',
    height: TOOL_HEIGHT.SQUARE,
    collapseOrder: COLLAPSE_ORDER.NEVER,
    component: TableCellColorTool,
    mobileComponent: MobileTableCellColorTool,
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const TABLE_ADD_COLUMN: ToolbarItemConfig = {
    id: 'TABLE_ADD_COLUMN',
    name: 'Add column',
    localeKey: 'tools.editing.table.addColumn',
    height: TOOL_HEIGHT.SQUARE,
    collapseOrder: COLLAPSE_ORDER.DEFAULT,
    component: AddColumnTool,
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const TABLE_ADD_ROW: ToolbarItemConfig = {
    id: 'TABLE_ADD_ROW',
    name: 'Add row',
    localeKey: 'tools.editing.table.addRow',
    height: TOOL_HEIGHT.SQUARE,
    collapseOrder: COLLAPSE_ORDER.DEFAULT,
    component: AddRowTool,
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const ASSIGNMENT: ToolbarItemConfig = {
    id: 'ASSIGNMENT',
    name: 'Assign',
    localeKey: 'tools.editing.assignment',
    height: TOOL_HEIGHT.SQUARE,
    collapseOrder: COLLAPSE_ORDER.DEFAULT,
    component: AssignUserTool,
    tooltipText: 'Assign to user',
    tooltipLocaleKey: 'tools.editing.tooltips.assignment',
    shortcut: ['shift', '@'],
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const INDENT_INCREASE: ToolbarItemConfig = {
    id: 'INDENT_INCREASE',
    name: 'Indent',
    localeKey: 'tools.editing.indent',
    height: TOOL_HEIGHT.SQUARE,
    collapseOrder: COLLAPSE_ORDER.DEFAULT,
    tooltipText: 'Indent',
    tooltipLocaleKey: 'tools.editing.tooltips.indent',
    shortcut: ['tab'],
    iconName: 'toolbar-indent',
    component: IndentTaskTool,
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

const INDENT_DECREASE: ToolbarItemConfig = {
    id: 'INDENT_DECREASE',
    name: 'Outdent',
    height: TOOL_HEIGHT.SQUARE,
    collapseOrder: COLLAPSE_ORDER.DEFAULT,
    tooltipText: 'Unindent',
    tooltipLocaleKey: 'tools.editing.tooltips.unindent',
    shortcut: ['shift', 'tab'],
    iconName: 'toolbar-unindent',
    component: UnindentTaskTool,
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

/**
 * Not a perfect solution, but this is a workaround to avoid the indent/unindent tools
 * showing on desktop — we currently have no specific "app mode" for tablet devices,
 * so the "app mode" says they should be available on both desktop and mobile,
 * and this predicate then filters them out on desktop, so they only show on mobile and tablet.
 */
const textIndentToolAvailabilityPredicate = () => {
    return (
        platformSingleton.formFactor === DeviceFormFactor.phone ||
        platformSingleton.formFactor === DeviceFormFactor.tablet
    );
};

const TEXT_INDENT_INCREASE: ToolbarItemConfig = {
    id: 'TEXT_INDENT_INCREASE',
    name: 'Indent',
    localeKey: 'tools.editing.textIndent',
    height: TOOL_HEIGHT.SQUARE,
    collapseOrder: TEXT_TOOL_COLLAPSE_ORDER.TEXT_INDENT,
    tooltipText: 'Indent',
    tooltipLocaleKey: 'tools.editing.tooltips.indent',
    shortcut: ['tab'],
    iconName: 'toolbar-indent',
    component: IndentTextTool,
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
    availabilityPredicate: textIndentToolAvailabilityPredicate,
};

const TEXT_INDENT_DECREASE: ToolbarItemConfig = {
    id: 'TEXT_INDENT_DECREASE',
    name: 'Unindent',
    localeKey: 'tools.editing.unindent',
    height: TOOL_HEIGHT.SQUARE,
    collapseOrder: TEXT_TOOL_COLLAPSE_ORDER.TEXT_INDENT,
    tooltipText: 'Unindent',
    tooltipLocaleKey: 'tools.editing.tooltips.unindent',
    shortcut: ['shift', 'tab'],
    iconName: 'toolbar-unindent',
    component: UnindentTextTool,
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
    availabilityPredicate: textIndentToolAvailabilityPredicate,
};

const DUE_DATE: ToolbarItemConfig = {
    id: 'DUE_DATE',
    name: 'Due date',
    localeKey: 'tools.editing.dueDate',
    height: TOOL_HEIGHT.SQUARE,
    collapseOrder: COLLAPSE_ORDER.DEFAULT,
    tooltipText: 'Set due date',
    tooltipLocaleKey: 'tools.editing.tooltips.dueDate',
    shortcut: ['command', 'shift', '@'],
    component: DueDateTool,
    appModeAvailability: [MilanoteApplicationMode.desktop, MilanoteApplicationMode.mobile],
};

export default {
    TEXT_STYLES,
    TEXT_BLOCK_STYLES,
    TEXT_BOLD,
    TEXT_ITALIC,
    TEXT_STRIKETHROUGH,
    TEXT_BULLET_LIST,
    TEXT_ORDERED_LIST,
    TEXT_CENTERED,
    TEXT_HYPERLINK,
    TEXT_INDENT_INCREASE,
    TEXT_INDENT_DECREASE,
    TEXT_UNDERLINE,
    TEXT_INLINE_CODE,
    TEXT_INLINE_COMMENT,

    TABLE_CELL_ALIGNMENT,
    TABLE_TEXT_STYLES,
    TABLE_CELL_FORMULA,
    TABLE_CELL_TYPE,
    TABLE_CELL_TYPE_FORMAT,
    TABLE_CELL_COLOR,
    TABLE_ADD_COLUMN,
    TABLE_ADD_ROW,

    ASSIGNMENT,
    INDENT_INCREASE,
    INDENT_DECREASE,
    DUE_DATE,
} as const;
